// src/lib/customers/useCustomers.tsx
//
// React-context-backed customer store. CRUD over CustomerAccount.
// In-memory + localStorage persistence so demo state survives reload.
//
// Bootstrap: seeded from DEMO_CUSTOMERS on first load (only if the
// store is empty / key absent). Subsequent writes are stored under
// `bench-customers-v1`.
//
// The store is intentionally minimal — Zustand would also work but
// would add a dependency. A useReducer + Context keeps the bundle
// unchanged.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import { DEMO_CUSTOMERS, type CustomerAccount } from "@/lib/demoData";

// ----- storage helpers ----------------------------------------------------

const STORAGE_KEY = "bench-customers-v1";

function loadFromStorage(): CustomerAccount[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((c) => typeof c === "object" && c && "id" in c)
    ) {
      return parsed as CustomerAccount[];
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(rows: CustomerAccount[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota / private-mode failures
  }
}

// ----- ID minting ---------------------------------------------------------

/**
 * Allocate the next customer id in the "C-1xxx" series. Looks at the
 * current row set so deletes don't free up reused numbers. Demo-
 * scope only — the real backend uses UUIDs.
 */
export function nextCustomerId(rows: CustomerAccount[]): string {
  let max = 1000;
  for (const row of rows) {
    const m = /^C-(\d+)$/.exec(row.id);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `C-${max + 1}`;
}

// ----- reducer ------------------------------------------------------------

export type CustomerAction =
  | { type: "create"; payload: CustomerAccount }
  | { type: "update"; payload: CustomerAccount }
  | { type: "delete"; payload: { id: string } }
  | { type: "replaceAll"; payload: CustomerAccount[] }
  | { type: "reset" };

function reducer(
  rows: CustomerAccount[],
  action: CustomerAction,
): CustomerAccount[] {
  switch (action.type) {
    case "create":
      // De-dupe by id (the form could conflict if the user typed one).
      if (rows.some((r) => r.id === action.payload.id)) return rows;
      return [action.payload, ...rows];
    case "update":
      return rows.map((r) =>
        r.id === action.payload.id ? action.payload : r,
      );
    case "delete":
      return rows.filter((r) => r.id !== action.payload.id);
    case "replaceAll":
      return action.payload;
    case "reset":
      return DEMO_CUSTOMERS;
  }
}

// ----- context ------------------------------------------------------------

export interface CustomersContextValue {
  customers: CustomerAccount[];
  getById: (id: string) => CustomerAccount | undefined;
  create: (input: Omit<CustomerAccount, "id"> & { id?: string }) => CustomerAccount;
  update: (row: CustomerAccount) => void;
  remove: (id: string) => void;
  reset: () => void;
}

const CustomersContext = createContext<CustomersContextValue | null>(null);

export function CustomersProvider({ children }: { children: ReactNode }) {
  // Lazy initializer — synchronous localStorage read so the first
  // render already has the persisted rows.
  const [rows, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadFromStorage() ?? DEMO_CUSTOMERS,
  );

  // Re-persist on every change. Throttling isn't necessary for a demo
  // store of ~30 rows.
  useEffect(() => {
    saveToStorage(rows);
  }, [rows]);

  const value = useMemo<CustomersContextValue>(
    () => ({
      customers: rows,
      getById: (id) => rows.find((r) => r.id === id),
      create: (input) => {
        const row: CustomerAccount = {
          id: input.id && input.id.length > 0 ? input.id : nextCustomerId(rows),
          companyName: input.companyName,
          contactName: input.contactName,
          email: input.email,
          country: input.country,
          pricingTier: input.pricingTier,
          monthlyLimitEUR: input.monthlyLimitEUR,
          shipTo: input.shipTo ?? [],
          activeSince: input.activeSince ?? new Date().toISOString().slice(0, 10),
        };
        dispatch({ type: "create", payload: row });
        return row;
      },
      update: (row) => dispatch({ type: "update", payload: row }),
      remove: (id) => dispatch({ type: "delete", payload: { id } }),
      reset: () => dispatch({ type: "reset" }),
    }),
    [rows],
  );

  return (
    <CustomersContext.Provider value={value}>
      {children}
    </CustomersContext.Provider>
  );
}

export function useCustomers(): CustomersContextValue {
  const ctx = useContext(CustomersContext);
  if (!ctx) {
    throw new Error(
      "useCustomers must be used inside a <CustomersProvider />",
    );
  }
  return ctx;
}

// Test/debug helper — drops the localStorage copy. Tests only.
export function _resetCustomersStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
